// =============================================================================
// CONNECTION INDICATOR
// =============================================================================
// Honest in-call connection feedback, derived entirely from the real
// RTCPeerConnection state that callStore mirrors from webrtcService. No fixed
// always-green placebo: the glyph and banner only ever reflect a state the
// browser actually reported.

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CallStatus } from '@chlann/shared';

// Number of quality bars to fill (0-3), derived from the real connection state.
// For a single peer this is exact; the W2 group grid will reduce per-peer states
// to a worst-case before passing one value in here.
function barLevel(
  state: RTCPeerConnectionState | null,
  callStatus: CallStatus
): 0 | 1 | 2 | 3 {
  switch (state) {
    case 'connected':
      return 3;
    case 'connecting':
    case 'new':
      return 2;
    case 'disconnected':
      return 1;
    case 'failed':
    case 'closed':
      return 0;
    default:
      // No peer state yet: fall back to the (also real) call status.
      return callStatus === 'active' ? 3 : 2;
  }
}

const BAR_HEIGHTS = ['h-1.5', 'h-2.5', 'h-3.5'];

// -----------------------------------------------------------------------------
// 3-bar quality glyph
// -----------------------------------------------------------------------------

export function ConnectionQualityBars({
  state,
  callStatus,
}: {
  state: RTCPeerConnectionState | null;
  callStatus: CallStatus;
}) {
  const { t } = useTranslation();
  const level = barLevel(state, callStatus);

  const colour =
    level === 3
      ? 'bg-success'
      : level === 1
        ? 'bg-accent'
        : level === 0
          ? 'bg-danger'
          : 'bg-ink-2';

  return (
    <span
      className="inline-flex items-end gap-0.5"
      role="img"
      aria-label={t('calls.connectionQuality', 'Connection quality')}
      title={t('calls.connectionQuality', 'Connection quality')}
    >
      {BAR_HEIGHTS.map((height, index) => (
        <span
          key={height}
          className={`w-1 rounded-sm ${height} ${index < level ? colour : 'bg-ink-4/40'}`}
        />
      ))}
    </span>
  );
}

// -----------------------------------------------------------------------------
// Transient connection banner (reconnecting / lost / restored)
// -----------------------------------------------------------------------------

export function ConnectionBanner({
  state,
}: {
  state: RTCPeerConnectionState | null;
}) {
  const { t } = useTranslation();
  const [showRestored, setShowRestored] = useState(false);
  const previous = useRef<RTCPeerConnectionState | null>(state);

  useEffect(() => {
    const was = previous.current;
    previous.current = state;

    // Recovered from a drop: announce it briefly, then clear.
    if ((was === 'disconnected' || was === 'failed') && state === 'connected') {
      setShowRestored(true);
      const timer = setTimeout(() => { setShowRestored(false); }, 3000);
      return () => { clearTimeout(timer); };
    }
  }, [state]);

  if (state === 'disconnected') {
    return (
      <Banner dotClass="bg-accent animate-pulse">
        {t('calls.reconnecting', 'Reconnecting...')}
      </Banner>
    );
  }

  if (state === 'failed') {
    return <Banner dotClass="bg-danger">{t('calls.connectionLost', 'Connection lost')}</Banner>;
  }

  if (showRestored) {
    return (
      <Banner dotClass="bg-success">{t('calls.connectionRestored', 'Connection restored')}</Banner>
    );
  }

  return null;
}

// Solid surface pill with ink-1 text so the message stays high-contrast in both
// light and dark themes; the connection state is carried by the coloured dot
// (accent = recovering, danger = lost, success = restored).
function Banner({ dotClass, children }: { dotClass: string; children: React.ReactNode }) {
  return (
    <div
      role="status"
      className="mb-2 flex items-center gap-2 rounded-full bg-surface-2 px-4 py-1.5 text-sm font-medium text-ink-1 shadow-md"
    >
      <span className={`h-2 w-2 rounded-full ${dotClass}`} />
      {children}
    </div>
  );
}
